import { useMemo } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import type { ApprovalStepConfig, InfoGateField, InfoGateStepConfig, WorkflowStepConfig } from '@kfolks/types'
import { Input } from '@/shared/components/atoms/ui/input'
import { Button } from '@/shared/components/atoms/ui/button'
import { MultiSourceAutocomplete } from './MultiSourceAutocomplete'
import { useWorkflowBuilderState } from './WorkflowBuilderState'

/**
 * Right-hand properties panel for the visual workflow builder. Renders
 * per-step-type editors for the currently-selected step that map exactly
 * to the persisted `WorkflowStepConfig` shape — no "approver resolver
 * strategy" strings, no XState-style fields.
 *
 * Shared editors (all step types):
 *   - `id` — read-only metadata (rename UX is a follow-up because every
 *     `after.next` reference would need a coordinated rewrite)
 *   - `label`, `slaHours`
 *   - `after` — single outgoing transition: `on` (success | failure) +
 *     `next` (target = another step id OR a terminal marker
 *     'completed' | 'rejected' | 'cancelled'). The shipped contract
 *     supports at most one `after` per step, so this is a single-target
 *     dropdown rather than a free-form canvas-edge action.
 *
 * Approval step (`type: 'approval'`):
 *   - `kind` ('decision' | 'action') — UX label only, engine-equivalent.
 *   - Four approver dimensions: `specificPeople` (employee UUIDs),
 *     `groups` (Entra group IDs), `submittersManager` (checkbox —
 *     resolves submitter.managerId at step entry), `collaboratorsManager`
 *     (checkbox — resolves the manager of the employee referenced by the
 *     request's `collaborator_id` form field). The engine OR-unions all
 *     four; at least one must be ticked-with-content (Zod-enforced).
 *   - `skipIfSubmitterIn: { people?, groups? }` — auto-skip when the
 *     submitter is in the listed people OR a member of a listed group
 *     (self-approval edge cases, e.g. HR manager submitting HR work).
 *
 * Info gate step (`type: 'info_gate'`):
 *   - `mode` shown read-only as `'manual'` (Phase 1 ships manual only).
 *   - `fields[]` mini-builder: each `InfoGateField` has `id`, `label`,
 *     `type` (constrained to the six allowed), `required`, `options`
 *     (visible only when `type === 'select'`).
 *
 * All edits flow through `updateStep(id, patch)` on the shared state.
 * Canonical "no zombie keys" invariant: removing the last item in any
 * list strips the empty array off the step.
 */
export function NodePropertiesPanel() {
	const { workflowConfig, selectedStepId, updateStep, removeStep } = useWorkflowBuilderState()
	const selected = useMemo(
		() => workflowConfig.steps.find((s) => s.id === selectedStepId) ?? null,
		[workflowConfig.steps, selectedStepId],
	)

	if (!selected) return <EmptySelectionState />
	return (
		<div className="space-y-4" aria-label={`Properties for ${selected.label || selected.id}`}>
			<HeaderRow step={selected} onRemove={() => removeStep(selected.id)} />
			<SharedSection step={selected} onPatch={(patch) => updateStep(selected.id, patch)} />
			<TransitionSection
				step={selected}
				allSteps={workflowConfig.steps}
				onPatch={(patch) => updateStep(selected.id, patch)}
			/>
			{selected.type === 'approval' ? (
				<ApprovalSection step={selected} onPatch={(patch) => updateStep(selected.id, patch)} />
			) : null}
			{selected.type === 'info_gate' ? (
				<InfoGateSection step={selected} onPatch={(patch) => updateStep(selected.id, patch)} />
			) : null}
		</div>
	)
}

// ─── Header ──────────────────────────────────────────────────────────────────

function HeaderRow({ step, onRemove }: { step: WorkflowStepConfig; onRemove: () => void }) {
	return (
		<div className="flex items-start justify-between gap-2 border-b pb-3">
			<div className="min-w-0 flex-1">
				<p className="font-mono text-xs text-muted-foreground">id: {step.id}</p>
				<p className="font-mono text-xs text-muted-foreground">type: {step.type}</p>
			</div>
			<Button variant="ghost" size="sm" onClick={onRemove} aria-label={`Remove step ${step.id}`}>
				<Trash2 className="size-4" />
			</Button>
		</div>
	)
}

// ─── Shared editors (label / slaHours) ───────────────────────────────────────

function SharedSection({
	step,
	onPatch,
}: {
	step: WorkflowStepConfig
	onPatch: (patch: Partial<WorkflowStepConfig>) => void
}) {
	return (
		<section className="space-y-3">
			<LabeledField label="Label" htmlFor="np-label">
				<Input
					id="np-label"
					type="text"
					value={step.label ?? ''}
					onChange={(e) => onPatch({ label: e.target.value } as never)}
				/>
			</LabeledField>
			<LabeledField label="SLA hours" htmlFor="np-sla">
				<Input
					id="np-sla"
					type="number"
					min={1}
					placeholder="e.g. 24"
					value={(step as { slaHours?: number }).slaHours ?? ''}
					onChange={(e) => {
						const v = e.target.value === '' ? undefined : Number.parseInt(e.target.value, 10)
						onPatch({ slaHours: Number.isNaN(v) ? undefined : v } as never)
					}}
				/>
			</LabeledField>
		</section>
	)
}

// ─── Transition (after.next) ─────────────────────────────────────────────────

const TERMINAL_MARKERS = ['completed', 'rejected', 'cancelled'] as const

function TransitionSection({
	step,
	allSteps,
	onPatch,
}: {
	step: WorkflowStepConfig
	allSteps: ReadonlyArray<WorkflowStepConfig>
	onPatch: (patch: Partial<WorkflowStepConfig>) => void
}) {
	const otherSteps = allSteps.filter((s) => s.id !== step.id)
	const after = step.after
	const setAfter = (next: WorkflowStepConfig['after'] | undefined) => {
		// Strip the key when undefined so the canonical shape stays clean.
		onPatch({ after: next } as never)
	}

	return (
		<section className="space-y-2 border-t pt-3">
			<h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Outgoing transition</h4>
			<p className="text-xs text-muted-foreground">
				Optional. When set, overrides the default "advance to the next step in the array" behavior.
			</p>
			<div className="grid grid-cols-2 gap-2">
				<LabeledField label="On" htmlFor="np-after-on">
					<select
						id="np-after-on"
						value={after?.on ?? ''}
						onChange={(e) => {
							const on = e.target.value as 'success' | 'failure' | ''
							if (on === '') {
								setAfter(undefined)
								return
							}
							setAfter({ on, next: after?.next ?? '' } as WorkflowStepConfig['after'])
						}}
						className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
					>
						<option value="">— None —</option>
						<option value="success">success</option>
						<option value="failure">failure</option>
					</select>
				</LabeledField>
				<LabeledField label="Next" htmlFor="np-after-next">
					<select
						id="np-after-next"
						value={after?.next ?? ''}
						disabled={!after?.on}
						onChange={(e) => {
							if (!after?.on) return
							const next = e.target.value
							if (next === '') {
								setAfter(undefined)
								return
							}
							setAfter({ on: after.on, next } as WorkflowStepConfig['after'])
						}}
						className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm disabled:opacity-50"
					>
						<option value="">— Choose target —</option>
						<optgroup label="Other steps">
							{otherSteps.map((s) => (
								<option key={s.id} value={s.id}>
									{s.id}
								</option>
							))}
						</optgroup>
						<optgroup label="Terminal markers">
							{TERMINAL_MARKERS.map((m) => (
								<option key={m} value={m}>
									{m}
								</option>
							))}
						</optgroup>
					</select>
				</LabeledField>
			</div>
		</section>
	)
}

// ─── Approval-specific editors ───────────────────────────────────────────────

function ApprovalSection({
	step,
	onPatch,
}: {
	step: ApprovalStepConfig
	onPatch: (patch: Partial<WorkflowStepConfig>) => void
}) {
	// `skipIfSubmitterIn` is now `{ people, groups }` — surface two separate
	// pickers and reconcile back to the structured shape on every change.
	// Strip empty arrays / collapse fully-empty object to `undefined` so
	// no zombie keys land in the persisted JSON.
	const setSkipPeople = (next: ReadonlyArray<string>) => {
		const groups = step.skipIfSubmitterIn?.groups ?? []
		const people = [...next]
		if (people.length === 0 && groups.length === 0) {
			onPatch({ skipIfSubmitterIn: undefined } as never)
			return
		}
		onPatch({ skipIfSubmitterIn: { people, groups } } as never)
	}
	const setSkipGroups = (next: ReadonlyArray<string>) => {
		const people = step.skipIfSubmitterIn?.people ?? []
		const groups = [...next]
		if (people.length === 0 && groups.length === 0) {
			onPatch({ skipIfSubmitterIn: undefined } as never)
			return
		}
		onPatch({ skipIfSubmitterIn: { people, groups } } as never)
	}

	return (
		<section className="space-y-4 border-t pt-3">
			<TypePresetSelector step={step} onPatch={onPatch} />
			<div>
				<h4 className="text-sm font-semibold">Who can approve?</h4>
				<p className="mt-1 text-xs text-muted-foreground">
					Pick one or more of the four options below. The request can be approved by anyone matched by any option you
					tick. <strong>At least one must be set.</strong>
				</p>
			</div>

			<ApproverDimensionCard
				title="Specific people"
				description="Name individual employees. Anyone in this list can approve."
				placeholder="Search employees by name…"
				inputAriaPrefix="specificPeople"
				dataSourceKey="employees.active"
				values={step.specificPeople ?? []}
				onChange={(next) => onPatch({ specificPeople: next.length ? [...next] : undefined } as never)}
			/>

			<ApproverDimensionCard
				title="Members of a group"
				description="Pick one or more groups (e.g. HR-Managers, Finance-Approvers). Any member of any listed group can approve."
				placeholder="Search groups by name…"
				inputAriaPrefix="groups"
				dataSourceKey="entra.groups"
				values={step.groups ?? []}
				onChange={(next) => onPatch({ groups: next.length ? [...next] : undefined } as never)}
			/>

			<ManagerCheckboxCard
				id="np-approval-submitters-manager"
				title="The submitter's manager"
				description="When ticked, the request is routed to the submitter's direct manager. If the submitter has no manager, the step falls back to System Fallback."
				checked={step.submittersManager === true}
				onChange={(checked) => onPatch({ submittersManager: checked ? true : undefined } as never)}
			/>

			<ManagerCheckboxCard
				id="np-approval-collaborators-manager"
				title="The collaborator's manager"
				description={
					'When ticked, the request is routed to the manager of the employee picked in the form (the "collaborator_id" field — e.g., the person being granted access). Wins over "the submitter\'s manager" if both are ticked. Has no effect when the form has no collaborator field, or when the collaborator is the submitter themselves.'
				}
				checked={step.collaboratorsManager === true}
				onChange={(checked) => onPatch({ collaboratorsManager: checked ? true : undefined } as never)}
			/>

			<div className="border-t pt-3">
				<h4 className="text-sm font-semibold">Skip when the submitter is in…</h4>
				<p className="mt-1 text-xs text-muted-foreground">
					Optional. Auto-skip this step when the submitter is one of the listed people, or a member of one of the listed
					groups. <strong>Useful for self-approval edge cases</strong> — for example, an HR manager submitting an
					HR-policy request shouldn't approve their own request, so add the HR-managers group here and the step skips
					for them.
				</p>
				<div className="mt-2 space-y-3">
					<ApproverDimensionCard
						title="People"
						description=""
						placeholder="Search people…"
						inputAriaPrefix="skipIfSubmitterInPeople"
						dataSourceKey="employees.active"
						values={step.skipIfSubmitterIn?.people ?? []}
						onChange={setSkipPeople}
					/>
					<ApproverDimensionCard
						title="Groups"
						description=""
						placeholder="Search groups…"
						inputAriaPrefix="skipIfSubmitterInGroups"
						dataSourceKey="entra.groups"
						values={step.skipIfSubmitterIn?.groups ?? []}
						onChange={setSkipGroups}
					/>
				</div>
			</div>
		</section>
	)
}

// ─── Manager checkbox card ──────────────────────────────────────────────────
/**
 * Visual parity with `ApproverDimensionCard` for the two manager-toggle
 * dimensions (`submittersManager`, `collaboratorsManager`). A checkbox
 * replaces the free-text picker that used to live here — the picker was
 * how `placeholder-eng-leadership-group` leaked into the seeded workflows
 * and broke SR-000133 (no value to type means no value to mis-type).
 */
function ManagerCheckboxCard({
	id,
	title,
	description,
	checked,
	onChange,
}: {
	id: string
	title: string
	description: string
	checked: boolean
	onChange: (checked: boolean) => void
}) {
	return (
		<div className="rounded-md border bg-card/40 p-3">
			<label htmlFor={id} className="flex items-start gap-3">
				<input
					id={id}
					type="checkbox"
					className="mt-1 size-4 shrink-0 rounded border-input"
					checked={checked}
					onChange={(e) => onChange(e.target.checked)}
				/>
				<div className="min-w-0 flex-1">
					<p className="text-sm font-semibold">{title}</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</label>
		</div>
	)
}

// ─── Type / preset selector ──────────────────────────────────────────────────

/**
 * "Type" dropdown at the top of the approval section.
 *
 * Two parts:
 *  1. **Type** — a `<select>` reflecting the persisted `kind` field
 *     (`'decision' | 'action'`). This is the source of truth and
 *     round-trips on save/reload.
 *  2. **Preset shortcuts** — named buttons (Manager decision, IT
 *     provisioning, Finance approval) that set `kind` to the right
 *     value AND show a hint about which approver-dimension card the
 *     preset is biased toward. They don't pre-fill arrays (no zombie
 *     keys) and don't persist a separate "preset" field — they're
 *     write-only convenience starting points. After applying, the
 *     admin sees Decision/Action in the Type dropdown.
 *
 * Engine semantics: `kind` is a UX label only. Both kinds resolve
 * approvers identically; this dropdown is intent-labeling, not a
 * behavioral switch.
 */
function TypePresetSelector({
	step,
	onPatch,
}: {
	step: ApprovalStepConfig
	onPatch: (patch: Partial<WorkflowStepConfig>) => void
}) {
	const kind = (step.kind ?? 'decision') as 'decision' | 'action'
	return (
		<div className="rounded-md border bg-card/40 p-3 space-y-3">
			<LabeledField label="Type" htmlFor="np-approval-kind">
				<select
					id="np-approval-kind"
					value={kind}
					onChange={(e) => onPatch({ kind: e.target.value as 'decision' | 'action' } as never)}
					className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
				>
					<option value="decision">Decision approval — someone decides whether it can proceed</option>
					<option value="action">Action approval — someone performs the work it asks for</option>
				</select>
			</LabeledField>

			<div className="space-y-1">
				<p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Quick presets</p>
				<div className="flex flex-wrap gap-1">
					{PRESET_OPTIONS.map((p) => (
						<button
							key={p.key}
							type="button"
							onClick={() => onPatch({ kind: p.kind } as never)}
							className="rounded border bg-background px-2 py-1 text-xs hover:bg-accent"
							title={p.hint}
							aria-label={`Apply ${p.label} preset`}
						>
							{p.label}
						</button>
					))}
				</div>
				<p className="text-[10px] text-muted-foreground/80">
					Presets set the type and suggest which approver card to fill. They never overwrite values you've already
					entered.
				</p>
			</div>
		</div>
	)
}

interface PresetOption {
	key: 'manager-decision' | 'it-provisioning' | 'finance-approval'
	label: string
	kind: 'decision' | 'action'
	hint: string
}

const PRESET_OPTIONS: ReadonlyArray<PresetOption> = [
	{
		key: 'manager-decision',
		label: 'Manager decision',
		kind: 'decision',
		hint: 'Sets type to Decision. Fill "The submitter\'s manager" with the group whose members should approve (e.g. eng-leadership-group).',
	},
	{
		key: 'it-provisioning',
		label: 'IT provisioning',
		kind: 'action',
		hint: 'Sets type to Action. Fill "Members of a group" with the IT / ops group that performs the provisioning.',
	},
	{
		key: 'finance-approval',
		label: 'Finance approval',
		kind: 'decision',
		hint: 'Sets type to Decision. Fill "Members of a group" with the finance-approvers group (or use "The submitter\'s manager" for a CFO ladder).',
	},
]

// ─── Approver-dimension card ─────────────────────────────────────────────────

/**
 * One of the approver-dimension authoring cards. Each card shows its
 * title, a plain-language explanation, and a name-search autocomplete
 * backed by a data source (`employees.active` for people-pickers,
 * `entra.groups` for the group picker). The autocomplete persists the
 * value's id and shows the human-readable label as a chip.
 */
function ApproverDimensionCard({
	title,
	description,
	placeholder,
	inputAriaPrefix,
	dataSourceKey,
	values,
	onChange,
}: {
	title: string
	description: string
	placeholder: string
	inputAriaPrefix: string
	dataSourceKey: string
	values: ReadonlyArray<string>
	onChange: (next: ReadonlyArray<string>) => void
}) {
	return (
		<div className="rounded-md border bg-card/50 p-3">
			{title ? <p className="text-sm font-medium">{title}</p> : null}
			{description ? <p className="mb-2 mt-0.5 text-xs text-muted-foreground">{description}</p> : null}
			<MultiSourceAutocomplete
				dataSourceKey={dataSourceKey}
				placeholder={placeholder}
				ariaPrefix={inputAriaPrefix}
				values={values}
				onChange={onChange}
			/>
		</div>
	)
}

// ─── Info-gate-specific editors ──────────────────────────────────────────────

const INFO_GATE_FIELD_TYPES = ['text', 'textarea', 'number', 'date', 'checkbox', 'select'] as const

function InfoGateSection({
	step,
	onPatch,
}: {
	step: InfoGateStepConfig
	onPatch: (patch: Partial<WorkflowStepConfig>) => void
}) {
	const fields = step.fields ?? []
	const setFields = (next: ReadonlyArray<InfoGateField>) => {
		onPatch({ fields: [...next] } as never)
	}

	return (
		<section className="space-y-3 border-t pt-3">
			<h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Info-gate fields</h4>
			<p className="text-xs text-muted-foreground">
				Phase 1 ships <span className="font-mono">mode: 'manual'</span> only — the submitter fills these fields to clear
				the gate. <span className="font-mono">'auto'</span> mode is rejected at the Zod layer until the connector
				framework lands.
			</p>
			<LabeledField label="Mode" htmlFor="np-info-mode">
				<Input id="np-info-mode" type="text" value={step.mode} disabled />
			</LabeledField>
			{fields.length === 0 ? (
				<p className="text-xs text-muted-foreground">No fields yet — click "Add field" to create the first one.</p>
			) : (
				<ul className="space-y-3">
					{fields.map((field, i) => (
						// Bare index key — the list is always patched in place; reordering
						// would re-mount and lose focus mid-edit.
						<li key={i} className="space-y-2 rounded-md border bg-card p-2">
							<div className="grid grid-cols-2 gap-2">
								<LabeledField label="Field id" htmlFor={`info-field-${i}-id`}>
									<Input
										id={`info-field-${i}-id`}
										type="text"
										aria-label={`Info-gate field ${i + 1} id`}
										placeholder="snake_case_id"
										value={field.id}
										onChange={(e) => {
											const next = [...fields]
											next[i] = { ...field, id: e.target.value }
											setFields(next)
										}}
									/>
								</LabeledField>
								<LabeledField label="Field label" htmlFor={`info-field-${i}-label`}>
									<Input
										id={`info-field-${i}-label`}
										type="text"
										aria-label={`Info-gate field ${i + 1} label`}
										value={field.label}
										onChange={(e) => {
											const next = [...fields]
											next[i] = { ...field, label: e.target.value }
											setFields(next)
										}}
									/>
								</LabeledField>
							</div>
							<div className="grid grid-cols-2 gap-2">
								<LabeledField label="Type" htmlFor={`info-field-${i}-type`}>
									<select
										id={`info-field-${i}-type`}
										aria-label={`Info-gate field ${i + 1} type`}
										value={field.type}
										onChange={(e) => {
											const next = [...fields]
											const newType = e.target.value as InfoGateField['type']
											next[i] = { ...field, type: newType }
											// Clearing options when leaving 'select' avoids zombie keys
											if (newType !== 'select' && next[i]?.options) delete next[i]!.options
											setFields(next)
										}}
										className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
									>
										{INFO_GATE_FIELD_TYPES.map((t) => (
											<option key={t} value={t}>
												{t}
											</option>
										))}
									</select>
								</LabeledField>
								<label className="flex items-center gap-2 self-end pb-2 text-sm">
									<input
										type="checkbox"
										aria-label={`Info-gate field ${i + 1} required`}
										checked={!!field.required}
										onChange={(e) => {
											const next = [...fields]
											next[i] = { ...field, required: e.target.checked || undefined }
											setFields(next)
										}}
										className="size-4 rounded border-input"
									/>
									Required
								</label>
							</div>
							{field.type === 'select' ? (
								<InfoGateOptionsEditor
									fieldIndex={i}
									options={field.options ?? []}
									onChange={(nextOptions) => {
										const next = [...fields]
										next[i] = { ...field, options: nextOptions.length ? [...nextOptions] : undefined }
										setFields(next)
									}}
								/>
							) : null}
							<div className="flex justify-end">
								<Button
									variant="ghost"
									size="sm"
									aria-label={`Remove info-gate field ${i + 1}`}
									onClick={() => setFields(fields.filter((_, idx) => idx !== i))}
								>
									<Trash2 className="size-4" /> Remove field
								</Button>
							</div>
						</li>
					))}
				</ul>
			)}
			<Button
				variant="outline"
				size="sm"
				onClick={() => setFields([...fields, { id: '', label: '', type: 'text' }])}
				aria-label="Add info-gate field"
			>
				<Plus className="size-4" /> Add field
			</Button>
		</section>
	)
}

function InfoGateOptionsEditor({
	fieldIndex,
	options,
	onChange,
}: {
	fieldIndex: number
	options: ReadonlyArray<{ label: string; value: string }>
	onChange: (next: ReadonlyArray<{ label: string; value: string }>) => void
}) {
	return (
		<div className="space-y-1 rounded-md border border-dashed p-2">
			<p className="text-xs font-medium text-muted-foreground">Options</p>
			{options.length === 0 ? (
				<p className="text-xs text-muted-foreground">No options yet — required for `select` fields.</p>
			) : (
				<ul className="space-y-1">
					{options.map((opt, i) => (
						<li key={i} className="flex items-center gap-2">
							<Input
								type="text"
								placeholder="Label"
								aria-label={`Info-gate field ${fieldIndex + 1} option ${i + 1} label`}
								value={opt.label}
								onChange={(e) => {
									const next = [...options]
									next[i] = { ...opt, label: e.target.value }
									onChange(next)
								}}
							/>
							<Input
								type="text"
								placeholder="value"
								aria-label={`Info-gate field ${fieldIndex + 1} option ${i + 1} value`}
								value={opt.value}
								onChange={(e) => {
									const next = [...options]
									next[i] = { ...opt, value: e.target.value }
									onChange(next)
								}}
							/>
							<Button
								variant="ghost"
								size="sm"
								aria-label={`Remove info-gate field ${fieldIndex + 1} option ${i + 1}`}
								onClick={() => onChange(options.filter((_, idx) => idx !== i))}
							>
								<Trash2 className="size-4" />
							</Button>
						</li>
					))}
				</ul>
			)}
			<Button
				variant="outline"
				size="sm"
				onClick={() => onChange([...options, { label: '', value: '' }])}
				aria-label={`Add option to info-gate field ${fieldIndex + 1}`}
			>
				<Plus className="size-4" /> Add option
			</Button>
		</div>
	)
}

// ─── Empty state + label helper ──────────────────────────────────────────────

function EmptySelectionState() {
	return (
		<div
			role="region"
			aria-label="Step properties"
			className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground"
		>
			<p className="font-medium">Select a step</p>
			<p className="mt-1">Click a step on the canvas to edit its properties.</p>
		</div>
	)
}

function LabeledField({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
	return (
		<label htmlFor={htmlFor} className="block space-y-1 text-sm">
			<span className="font-medium text-foreground">{label}</span>
			{children}
		</label>
	)
}
